import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  PlayCircle, Calendar, MapPin, ArrowRight } from 'lucide-react';

const USC: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Southern California";

    return (
        <div className="min-h-screen bg-[#F0F0F0] font-sans text-[#1c1c1c] selection:bg-[#990000] selection:text-[#FFCC00]">
             {/* Header */}
            <nav className="bg-[#990000] py-4 px-6 md:px-12 sticky top-0 z-50 shadow-md">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3 text-white">
                        <span className="font-serif text-3xl font-bold tracking-wide">{schoolName.split(' ')[0]}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-white text-sm uppercase tracking-wider">
                        <a href="#" className="hover:text-[#FFCC00] transition-colors">{schoolName}</a>
                    </div>
                    
                    <div className="flex gap-6 items-center text-white">
                         <div className="hidden md:flex gap-6 font-bold text-xs uppercase tracking-widest">
                            <a href="#" className="hover:text-[#FFCC00]">About</a>
                            <a href="#" className="hover:text-[#FFCC00]">Admissions</a>
                            <a href="#" className="hover:text-[#FFCC00]">Academics</a>
                        </div>
                        <Search className="cursor-pointer hover:text-[#FFCC00]" size={22} />
                        <Menu className="cursor-pointer lg:hidden hover:text-[#FFCC00]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Cinematic */}
            <header className="relative h-[700px]">
                 <img 
                    src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop" 
                    alt="Tommy Trojan" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-24 pb-24 text-white">
                     <span className="bg-[#FFCC00] text-[#990000] font-bold uppercase tracking-widest text-xs px-2 py-1 mb-6 inline-block">Fight On</span>
                     <h1 className="text-5xl md:text-8xl font-serif font-bold mb-8 leading-none">
                         Leading the Way.
                     </h1>
                     <button className="flex items-center gap-4 group">
                         <PlayCircle size={64} className="text-[#FFCC00] group-hover:scale-110 transition-transform" />
                         <span className="font-bold text-xl uppercase tracking-widest group-hover:text-[#FFCC00] transition-colors">Watch the Video</span>
                     </button>
                </div>
            </header>

            {/* News - Grid with Gold Accents */}
            <section className="bg-white py-24 px-6 md:px-12 relative -mt-16 z-10 max-w-[1300px] mx-auto shadow-xl rounded-t-lg">
                <div className="text-center mb-16">
                     <h2 className="text-4xl font-serif font-bold text-[#990000] mb-4">{schoolName.split(' ')[0]} News</h2>
                     <div className="w-24 h-1 bg-[#FFCC00] mx-auto"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                     <div className="col-span-1 md:col-span-2 group cursor-pointer">
                         <div className="overflow-hidden rounded-sm mb-6">
                            <img src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2070&auto=format&fit=crop" className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110" alt="Main Story" />
                         </div>
                         <h3 className="text-3xl font-serif font-bold text-[#1c1c1c] mb-4 group-hover:text-[#990000] transition-colors">
                             Viterbi School engineers develop AI for sustainable urban planning
                         </h3>
                         <p className="text-gray-600 text-lg leading-relaxed">
                             New models predict city growth patterns to minimize environmental impact.
                         </p>
                     </div>

                     <div className="flex flex-col gap-10">
                         {[
                             `${schoolName} Games ranked #1 in North America for 15th consecutive year`,
                             "Thornton School of Music announces star-studded jazz festival lineup",
                             `Keck Medicine of ${schoolName} performs historic robotic surgery`,
                             "Marshall School of Business launches new global supply chain initiative"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer border-b border-gray-100 pb-8 last:border-0 relative pl-6">
                                 <div className="absolute left-0 top-2 w-1 h-full bg-[#FFCC00] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                 <h4 className="text-lg font-bold text-[#1c1c1c] leading-tight group-hover:text-[#990000] transition-colors mb-2">{title}</h4>
                                 <span className="text-gray-400 text-xs font-bold uppercase tracking-widest">Read More</span>
                             </div>
                         ))}
                     </div>
                </div>
            </section>

             {/* Events / Community */}
             <section className="bg-[#1c1c1c] text-white py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <h2 className="text-3xl font-serif font-bold text-[#FFCC00] mb-12">Happening at {schoolName}</h2>

                     <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                         <div className="bg-[#333] p-6 rounded-sm border-t-4 border-[#990000] hover:bg-[#444] transition-colors cursor-pointer group">
                             <Calendar size={32} className="text-[#FFCC00] mb-4" />
                             <span className="text-sm font-bold text-gray-400 mb-1 block">OCT 25</span>
                             <h3 className="text-lg font-bold mb-2 group-hover:text-[#FFCC00]">Homecoming</h3>
                             <p className="text-sm text-gray-400">Join the Trojan Family for a weekend of celebration.</p>
                         </div>
                          <div className="bg-[#333] p-6 rounded-sm border-t-4 border-[#990000] hover:bg-[#444] transition-colors cursor-pointer group">
                             <MapPin size={32} className="text-[#FFCC00] mb-4" />
                             <span className="text-sm font-bold text-gray-400 mb-1 block">Daily</span>
                             <h3 className="text-lg font-bold mb-2 group-hover:text-[#FFCC00]">Campus Tours</h3>
                             <p className="text-sm text-gray-400">Explore our beautiful University Park Campus.</p>
                         </div>
                          <div className="col-span-1 md:col-span-2 bg-[#990000] p-8 flex flex-col justify-center items-start rounded-sm relative overflow-hidden group cursor-pointer">
                              <div className="absolute right-0 bottom-0 opacity-10">
                                   {/* Trojan Helmet Icon implication */}
                                   <div className="text-9xl font-serif font-bold text-black">{schoolName.split(' ')[0]}</div>
                              </div>
                              <h3 className="text-2xl font-bold mb-4 relative z-10">Admission & Aid</h3>
                              <p className="mb-8 max-w-sm relative z-10 text-[#FFCC00]">We are looking for students who are academically ambitious and eager to make an impact.</p>
                              <Button className="bg-white text-[#990000] hover:bg-[#FFCC00] hover:text-[#1c1c1c] font-bold uppercase tracking-widest relative z-10">
                                  Apply Now
                              </Button>
                         </div>
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#f8f8f8] text-[#333] py-16 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 font-sans text-sm">
                      <div className="col-span-1 md:col-span-2">
                          <span className="bg-[#990000] text-[#FFCC00] px-2 py-1 text-2xl font-serif font-bold inline-block mb-6">{schoolName.split(' ')[0]}</span>
                          <p className="text-gray-600 mb-2">{schoolName}</p>
                          <p className="text-gray-600">Los Angeles, CA 90089</p>
                      </div>

                      <div>
                          <h4 className="font-bold text-[#990000] uppercase tracking-widest mb-6 text-xs">Schools</h4>
                          <ul className="space-y-3 font-bold text-gray-500">
                              <li><a href="#" className="hover:text-[#990000]">Arts & Sciences</a></li>
                              <li><a href="#" className="hover:text-[#990000]">Business</a></li>
                              <li><a href="#" className="hover:text-[#990000]">Cinematic Arts</a></li>
                              <li><a href="#" className="hover:text-[#990000]">Engineering</a></li>
                          </ul>
                      </div>

                       <div>
                          <h4 className="font-bold text-[#990000] uppercase tracking-widest mb-6 text-xs">Resources</h4>
                          <ul className="space-y-3 font-bold text-gray-500">
                              <li><a href="#" className="hover:text-[#990000]">Careers</a></li>
                              <li><a href="#" className="hover:text-[#990000]">Emergency Information</a></li>
                              <li><a href="#" className="hover:text-[#990000]">Privacy Policy</a></li>
                              <li><a href="#" className="hover:text-[#990000]">Notice of Non-Discrimination</a></li>
                          </ul>
                      </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-400">
                     © 2026 {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default USC;
